import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { UrlService } from './url.service';
import { QuestionnaireQuestionInstancesDto } from 'app/@types/dto/questionnaire-question-instances-dto.model';
import { QuestionInstanceDto } from 'app/@types/dto/question-instance-dto.model';
import { ResultInstanceDto } from 'app/@types/dto/result-instance-dto.model';
import { SolutionInstanceDto } from 'app/@types/dto/solution-instance-dto.model';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { SectorBenchmarkDto } from 'app/@types/dto/sector-benchmark-dto.model';
import { CurrencyDto } from 'app/@types/dto/currency-dto.model';

@Injectable({
    providedIn: 'root',
})
export class QuestionnaireHttpService {
    constructor(private http: HttpClient, private urlService: UrlService) {}

    getOrganization(orgId: string): Observable<OrganizationDto> {
        return this.http.get<OrganizationDto>(this.urlService.getOrganizationUrl(orgId));
    }

    get(orgId: string, year: number, entityId: string): Observable<QuestionnaireQuestionInstancesDto> {
        return this.http.get<QuestionnaireQuestionInstancesDto>(
            this.urlService.getQuestionnaire(orgId, year, entityId),
        );
    }

    getAll(orgId: string): Observable<QuestionnaireQuestionInstancesDto[]> {
        return this.http.get<QuestionnaireQuestionInstancesDto[]>(this.urlService.getAllQuestionnaires(orgId));
    }

    createUpdate(orgId: string, qi: QuestionInstanceDto): Observable<QuestionInstanceDto[]> {
        return this.http.put<QuestionInstanceDto[]>(this.urlService.createUpdateQuestionInstance(orgId), qi);
    }

    createQuestionnaire(orgId: string, questionnaire: Partial<QuestionnaireDto>): Observable<QuestionnaireDto> {
        return this.http.put<QuestionnaireDto>(this.urlService.createQuestionnaire(orgId), questionnaire);
    }

    createQuestionnaireFromPeriod(orgId: string, year: number): Observable<QuestionnaireDto[]> {
        return this.http.put<QuestionnaireDto[]>(this.urlService.createQuestionnaireFromPeriod(orgId), year);
    }

    createEntity(orgId: string, entity: OrganizationEntityDto): Observable<OrganizationEntityDto> {
        return this.http.post<OrganizationEntityDto>(this.urlService.getOrganizationEntityUrl(orgId), entity);
    }

    updateEntity(orgId: string, orgEntityId: string, entity: OrganizationEntityDto): Observable<OrganizationEntityDto> {
        return this.http.put<OrganizationEntityDto>(
            this.urlService.getOrganizationEntityUrl(orgId, orgEntityId),
            entity,
        );
    }

    deleteEntity(orgId: string, entityId: string): Observable<QuestionnaireDto[]> {
        return this.http.delete<QuestionnaireDto[]>(this.urlService.getOrganizationEntityUrl(orgId, entityId));
    }

    getEntities(orgId: string): Observable<OrganizationEntityDto[]> {
        return this.http.get<OrganizationEntityDto[]>(this.urlService.getOrganizationEntityUrl(orgId));
    }

    deleteQuestionnaireFromPeriod(orgId: string, year: number): Observable<QuestionnaireDto[]> {
        return this.http.put<QuestionnaireDto[]>(this.urlService.deleteQuestionnaireFromPeriod(orgId), year);
    }

    editQuestionnairesPeriod(orgId: string, currentyear: number, newYear: number): Observable<QuestionnaireDto[]> {
        return this.http.put<QuestionnaireDto[]>(this.urlService.editQuestionnairesPeriod(orgId), {
            currentyear,
            newYear,
        });
    }

    deleteQuestionnaire(orgId: string, questionnaireId: string) {
        return this.http.delete(this.urlService.deleteQuestionnaire(orgId, questionnaireId));
    }

    hardDeleteQuestionnaire(orgId: string, questionnaireId: string) {
        return this.http.delete(this.urlService.hardDeleteQuestionnaire(orgId, questionnaireId));
    }

    updateProperty(orgId: string, questionnaire: QuestionnaireDto): Observable<QuestionnaireDto> {
        return this.http.put<QuestionnaireDto>(this.urlService.updateQuestionnaire(orgId), questionnaire);
    }

    delete(orgId: string, qi: QuestionInstanceDto): Observable<QuestionInstanceDto[]> {
        return this.http.delete<QuestionInstanceDto[]>(this.urlService.deleteQuestionInstance(orgId), { body: qi });
    }

    getResults(orgId: string, questionnaireId: string): Observable<ResultInstanceDto[]> {
        return this.http.get<ResultInstanceDto[]>(this.urlService.getResults(orgId, questionnaireId));
    }

    getBenchmark(sectorId: string, entityId: string): Observable<SectorBenchmarkDto> {
        return this.http.get<SectorBenchmarkDto>(this.urlService.getBenchmark(sectorId, entityId));
    }

    getSolutions(orgId: string, questionnaireId: string): Observable<SolutionInstanceDto[]> {
        return this.http.get<SolutionInstanceDto[]>(this.urlService.getSolutions(orgId, questionnaireId));
    }

    getCurrencies(orgId: string): Observable<CurrencyDto[]> {
        return this.http.get<CurrencyDto[]>(this.urlService.getCurrencies(orgId));
    }
}
